import { useEffect, useState, useCallback } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import {
  Check, X, Clock, ArrowCounterClockwise, CheckSquare, Square, MinusSquare,
  MagnifyingGlass, CaretDown, CaretUp, Bank, Receipt,
} from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import Pagination from "./Pagination";
import ExportButtons from "./ExportButtons";

function formatINR(num) {
  return `₹ ${Number(num || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return "-";
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "< 1h ago";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function statusBadge(status) {
  const styles = {
    pending: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20",
    auto_approved: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20",
    approved: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20",
    paid: "bg-[#A855F7]/10 text-[#A855F7] border-[#A855F7]/20",
    rejected: "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20",
  };
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border ${styles[status] || ""}`}>
      {(status || "").replace("_", " ")}
    </Badge>
  );
}

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "auto_approved", label: "Auto-approved" },
  { value: "approved", label: "Approved" },
  { value: "paid", label: "Paid" },
  { value: "rejected", label: "Rejected" },
];

export default function WithdrawalQueue() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const pageSize = 8;
  const ws = useWebSocket();
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [runningPayout, setRunningPayout] = useState(false);

  const loadPayoutSummary = useCallback(() => {
    axios.get(`${API}/admin/payouts/summary`, { headers: getAuthHeaders() })
      .then(r => setPayoutSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPayoutSummary(); }, [loadPayoutSummary]);

  // Debounce search input 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when search/status changes
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter]);

  const runPayouts = async () => {
    setRunningPayout(true);
    try {
      const r = await axios.post(`${API}/admin/payouts/run`, {}, { headers: getAuthHeaders() });
      toast.success(`Payout sweep done — ${r.data.processed} withdrawal(s) processed`);
      loadPayoutSummary();
      fetchWithdrawals(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payout run failed");
    } finally {
      setRunningPayout(false);
    }
  };

  const actionableIds = withdrawals
    .filter((w) => w.status === "pending" || w.status === "auto_approved")
    .map((w) => w.withdrawal_id);
  const selectedCount = selected.size;
  const allActionableSelected = actionableIds.length > 0 && actionableIds.every((id) => selected.has(id));
  const someActionableSelected = actionableIds.some((id) => selected.has(id));

  const fetchWithdrawals = useCallback((p) => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(p || page),
      page_size: String(pageSize),
    });
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (statusFilter) params.set("status", statusFilter);
    axios
      .get(`${API}/admin/withdrawals?${params}`, { headers: getAuthHeaders() })
      .then((res) => {
        setWithdrawals(res.data.items || []);
        setTotal(res.data.total || 0);
        setSelected(new Set());
      })
      .catch((err) => logger.error("Withdrawals error:", err))
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => { fetchWithdrawals(page); }, [page, fetchWithdrawals]);

  useEffect(() => {
    if (!ws) return;
    const unsub = ws.subscribe("withdrawal_update", () => fetchWithdrawals(page));
    return unsub;
  }, [ws, fetchWithdrawals, page]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllActionable = () => {
    if (allActionableSelected) setSelected(new Set());
    else setSelected(new Set(actionableIds));
  };

  const handleAction = async (id, action) => {
    setProcessing(id);
    try {
      await axios.post(`${API}/admin/withdrawals/${id}/action`, { action }, { headers: getAuthHeaders() });
      toast.success(`Withdrawal ${action}d successfully`);
      fetchWithdrawals(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} withdrawal`);
    } finally {
      setProcessing(null);
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    setBulkProcessing(true);
    try {
      const res = await axios.post(
        `${API}/admin/withdrawals/bulk-action`,
        { withdrawal_ids: Array.from(selected), action },
        { headers: getAuthHeaders() }
      );
      toast.success(`${res.data.processed} withdrawals ${action}d (${formatINR(res.data.total_amount)})`);
      fetchWithdrawals(page);
    } catch (err) {
      toast.error(`Bulk ${action} failed`);
    } finally {
      setBulkProcessing(false);
    }
  };

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="withdrawal-queue">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Withdrawal Queue
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5" data-testid="withdrawal-total-count">
            {total} {debouncedSearch || statusFilter ? "matching" : "total"} request{total === 1 ? "" : "s"}
            {debouncedSearch && (
              <span className="ml-1 text-[#00E5FF]">· searching &quot;{debouncedSearch}&quot;</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons exportType="withdrawals" title="Withdrawals" />
          <button onClick={() => fetchWithdrawals(page)} data-testid="refresh-withdrawals" className="p-2 rounded-md hover:bg-white/5 transition-colors">
            <ArrowCounterClockwise size={16} className="text-[#A1A1AA]" />
          </button>
        </div>
      </div>

      {/* Cashfree payout status bar */}
      {payoutSummary && (
        <div data-testid="payout-summary-bar" className="flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-md border border-white/10 bg-black/30 flex-wrap">
          <div className="flex items-center gap-4 text-[11px] flex-wrap">
            <span className="uppercase tracking-wider text-[#A1A1AA] font-bold">Cashfree Payouts</span>
            {payoutSummary.stub_mode && (
              <span data-testid="payout-stub-badge" className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-bold text-[10px]">STUB MODE (no live keys)</span>
            )}
            <span className="text-[#A1A1AA]">Awaiting: <b className="text-white">{payoutSummary.awaiting_payout}</b></span>
            <span className="text-emerald-300">Paid: <b>{payoutSummary.paid}</b></span>
            <span className="text-rose-300">Failed: <b>{payoutSummary.failed}</b></span>
            <span className="text-[#737373]">Auto-credit day: {payoutSummary.credit_day}th · {payoutSummary.env}</span>
          </div>
          <button data-testid="run-payouts-btn" onClick={runPayouts} disabled={runningPayout}
            className="px-3 py-1.5 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors disabled:opacity-50">
            {runningPayout ? "Running..." : "Run payouts now"}
          </button>
        </div>
      )}

      {/* Search + Status filter */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <Input
            data-testid="withdrawal-search-input"
            placeholder="Search by Partner ID (VAT-000043), user ID, name, email, phone, UPI, account number, IFSC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-black/40 border-white/10 text-white text-sm placeholder:text-[#737373]"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="withdrawal-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white p-1"
              title="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="withdrawal-status-filter">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || "all"}
              data-testid={`status-filter-${f.value || "all"}`}
              onClick={() => setStatusFilter(f.value)}
              className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-wider font-bold border transition-colors ${
                statusFilter === f.value
                  ? "bg-[#00E5FF] text-black border-[#00E5FF]"
                  : "bg-transparent text-[#A1A1AA] border-white/10 hover:border-white/30 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedCount > 0 && (
        <div
          className="flex items-center justify-between mb-3 px-4 py-2.5 rounded-md border border-[#00E5FF]/20"
          style={{ background: "rgba(0,229,255,0.05)" }}
          data-testid="bulk-action-bar"
        >
          <span className="text-xs text-[#00E5FF] font-medium">
            {selectedCount} withdrawal{selectedCount > 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-approve-btn"
              onClick={() => handleBulkAction("approve")}
              disabled={bulkProcessing}
              className="px-3 py-1.5 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
            >
              <Check size={14} weight="bold" className="inline mr-1" />
              Approve All
            </button>
            <button
              data-testid="bulk-reject-btn"
              onClick={() => handleBulkAction("reject")}
              disabled={bulkProcessing}
              className="px-3 py-1.5 rounded-md text-xs font-bold text-[#EF4444] border border-[#EF4444]/20 bg-[#EF4444]/10 hover:bg-[#EF4444]/20 transition-colors disabled:opacity-50"
            >
              <X size={14} weight="bold" className="inline mr-1" />
              Reject All
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="px-2 py-1.5 rounded-md text-xs text-[#A1A1AA] hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : total === 0 ? (
        <div className="h-40 flex flex-col items-center justify-center text-center" data-testid="withdrawals-empty">
          <p className="text-sm text-[#A1A1AA]">No withdrawals found.</p>
          {(debouncedSearch || statusFilter) && (
            <button
              onClick={() => { setSearch(""); setStatusFilter(""); }}
              className="mt-2 text-[11px] text-[#00E5FF] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-white/10 hover:bg-transparent">
                  <TableHead className="w-10">
                    <button
                      data-testid="select-all-pending"
                      onClick={toggleSelectAllActionable}
                      className="text-[#A1A1AA] hover:text-[#00E5FF] transition-colors"
                      title={allActionableSelected ? "Deselect all" : "Select all actionable"}
                    >
                      {allActionableSelected ? (
                        <CheckSquare size={16} weight="duotone" className="text-[#00E5FF]" />
                      ) : someActionableSelected ? (
                        <MinusSquare size={16} weight="duotone" className="text-[#00E5FF]" />
                      ) : (
                        <Square size={16} />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] w-8" />
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">User · ID</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Amount · Net</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Requested</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {withdrawals.map((w) => {
                  const isActionable = w.status === "pending" || w.status === "auto_approved";
                  const isExpanded = expanded.has(w.withdrawal_id);
                  const dest = w.destination || {};
                  const net = w.net_payout ?? w.fees?.net_payout;
                  const rows = [
                    <TableRow
                      key={w.withdrawal_id}
                      className={`border-b border-white/5 transition-colors ${
                        selected.has(w.withdrawal_id) ? "bg-[#00E5FF]/5" : "hover:bg-white/[0.03]"
                      }`}
                      data-testid={`withdrawal-row-${w.withdrawal_id}`}
                    >
                        <TableCell className="w-10">
                          {isActionable ? (
                            <Checkbox
                              data-testid={`select-${w.withdrawal_id}`}
                              checked={selected.has(w.withdrawal_id)}
                              onCheckedChange={() => toggleSelect(w.withdrawal_id)}
                              className="border-white/20 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:border-[#00E5FF]"
                            />
                          ) : (
                            <span className="w-4 h-4 block" />
                          )}
                        </TableCell>
                        <TableCell className="w-8">
                          <button
                            data-testid={`expand-${w.withdrawal_id}`}
                            onClick={() => toggleExpand(w.withdrawal_id)}
                            className="text-[#A1A1AA] hover:text-[#00E5FF]"
                            title={isExpanded ? "Hide details" : "Show full details"}
                          >
                            {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm text-white">{w.user_name || "—"}</p>
                            <p className="text-[10px] text-[#A1A1AA] font-mono truncate max-w-[240px]" title={w.user_email}>{w.user_email || "—"}</p>
                            <p className="text-[10px] text-[#00E5FF] font-mono font-bold mt-0.5" title={`Partner ID: ${w.display_id || "—"}`}>
                              {w.display_id || "—"}
                            </p>
                            <p className="text-[9px] text-[#737373] font-mono truncate max-w-[240px]" title={w.withdrawal_id}>{w.withdrawal_id}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="text-sm text-white font-bold font-mono">{formatINR(w.amount)}</span>
                            {net != null && net !== w.amount && (
                              <p className="text-[10px] text-emerald-400 font-mono">Net: {formatINR(net)}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{statusBadge(w.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
                            <Clock size={12} />
                            {timeAgo(w.requested_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {isActionable ? (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                data-testid={`approve-${w.withdrawal_id}`}
                                onClick={() => handleAction(w.withdrawal_id, "approve")}
                                disabled={processing === w.withdrawal_id}
                                className="px-3 py-1.5 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
                              >
                                <Check size={14} weight="bold" className="inline mr-1" />
                                Approve
                              </button>
                              <button
                                data-testid={`reject-${w.withdrawal_id}`}
                                onClick={() => handleAction(w.withdrawal_id, "reject")}
                                disabled={processing === w.withdrawal_id}
                                className="px-3 py-1.5 rounded-md text-xs font-bold text-[#EF4444] border border-[#EF4444]/20 bg-[#EF4444]/10 hover:bg-[#EF4444]/20 transition-colors disabled:opacity-50"
                              >
                                <X size={14} weight="bold" className="inline mr-1" />
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-[#A1A1AA]">Processed</span>
                          )}
                        </TableCell>
                      </TableRow>,
                  ];
                  if (isExpanded) {
                    rows.push(
                      <TableRow key={`${w.withdrawal_id}-detail`} className="bg-black/30 hover:bg-black/30" data-testid={`detail-${w.withdrawal_id}`}>
                          <TableCell colSpan={7} className="py-4">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-[11px]">
                              {/* User */}
                              <div className="rounded-md border border-white/10 bg-black/40 p-3">
                                <p className="text-[9px] font-bold uppercase tracking-wider text-[#00E5FF] mb-2">User</p>
                                <DetailRow k="Partner ID" v={w.display_id} mono highlight />
                                <DetailRow k="User ID" v={w.user_id} mono />
                                <DetailRow k="Name" v={w.user_name} />
                                <DetailRow k="Email" v={w.user_email} mono />
                                <DetailRow k="Phone" v={w.user_phone} mono />
                                <DetailRow k="WhatsApp" v={w.user_whatsapp} mono />
                                <DetailRow k="Reg. UPI" v={w.user_upi_id} mono />
                              </div>

                              {/* Destination */}
                              <div className="rounded-md border border-white/10 bg-black/40 p-3">
                                <div className="flex items-center gap-1 mb-2">
                                  <Bank size={11} weight="duotone" className="text-[#A855F7]" />
                                  <p className="text-[9px] font-bold uppercase tracking-wider text-[#A855F7]">Destination ({dest.type || "—"})</p>
                                </div>
                                {dest.type === "upi" ? (
                                  <>
                                    <DetailRow k="UPI ID" v={dest.upi_id} mono />
                                    <DetailRow k="Holder" v={dest.holder_name} />
                                  </>
                                ) : (
                                  <>
                                    <DetailRow k="Holder" v={dest.holder_name || w.bank_details?.account_holder_name} />
                                    <DetailRow k="Bank" v={dest.bank_name || w.bank_details?.bank_name} />
                                    <DetailRow k="Account" v={dest.account_number || w.bank_details?.account_number} mono />
                                    <DetailRow k="IFSC" v={dest.ifsc_code || w.bank_details?.ifsc_code} mono />
                                  </>
                                )}
                                {dest.name_match_score != null && (
                                  <DetailRow
                                    k="Name match"
                                    v={`${(dest.name_match_score * 100).toFixed(0)}% · ${dest.name_matched ? "MATCH" : "MISMATCH"}`}
                                  />
                                )}
                                {dest.verified_via && <DetailRow k="Verified via" v={dest.verified_via} />}
                                {w.probation && (
                                  <div className="mt-2 pt-2 border-t border-amber-500/30">
                                    <p className="text-[9px] uppercase tracking-wider text-amber-400 mb-1">Probation</p>
                                    <DetailRow k="Trial #" v={`${w.probation.trial_number}/${w.probation.trial_max}`} highlight />
                                    <DetailRow k="Limit/txn" v={`₹${(w.probation.trial_limit_inr || 0).toLocaleString("en-IN")}`} mono />
                                    {w.probation.graduating_now && <DetailRow k="Status" v="Graduating after this" highlight />}
                                  </div>
                                )}
                              </div>

                              {/* Fees + Decision */}
                              <div className="rounded-md border border-white/10 bg-black/40 p-3">
                                <div className="flex items-center gap-1 mb-2">
                                  <Receipt size={11} weight="duotone" className="text-[#10B981]" />
                                  <p className="text-[9px] font-bold uppercase tracking-wider text-[#10B981]">Fees & Decision</p>
                                </div>
                                {w.fees && (
                                  <>
                                    <DetailRow k="Gross" v={formatINR(w.fees.gross_amount)} mono />
                                    <DetailRow k={`GST (${w.fees.gst_rate_pct}%)`} v={`– ${formatINR(w.fees.gst_amount)}`} mono />
                                    <DetailRow k="Admin fee" v={`– ${formatINR(w.fees.admin_fee)}`} mono />
                                    <DetailRow k="Net payout" v={formatINR(w.fees.net_payout)} mono highlight />
                                  </>
                                )}
                                {w.scheduled_credit_date && (
                                  <div className="pt-1 border-t border-[#27272A]">
                                    <p className="text-[9px] uppercase tracking-wider text-[#737373] mb-1">Scheduled Credit</p>
                                    <p className="text-[10px] text-amber-300 font-bold" data-testid="scheduled-credit-date">{w.scheduled_credit_date}</p>
                                  </div>
                                )}
                                {w.payout && (
                                  <div className="pt-1 border-t border-[#27272A]" data-testid={`payout-info-${w.withdrawal_id}`}>
                                    <p className="text-[9px] uppercase tracking-wider text-[#737373] mb-1">Cashfree Payout</p>
                                    <p className={`text-[10px] font-bold ${w.payout.status === "SUCCESS" ? "text-emerald-300" : w.payout.status === "FAILED" ? "text-rose-300" : "text-amber-300"}`}>
                                      {w.payout.status}{w.payout.stub ? " (stub)" : ""}{w.payout.utr ? ` · UTR ${w.payout.utr}` : ""}
                                    </p>
                                    {w.payout.error && <p className="text-[9px] text-rose-400 mt-0.5">{w.payout.error}</p>}
                                  </div>
                                )}
                                {w.decision_reason && (
                                  <div className="mt-2 pt-2 border-t border-white/10">
                                    <p className="text-[9px] uppercase tracking-wider text-[#737373] mb-1">Decision</p>
                                    <p className="text-[10px] text-[#D4D4D8] leading-snug">{w.decision_reason}</p>
                                    {w.trust_distinct_days != null && (
                                      <p className="text-[10px] text-[#737373] mt-1">Trust: {w.trust_distinct_days}/5 distinct days</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                    );
                  }
                  return rows;
                })}
              </TableBody>
            </Table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

function DetailRow({ k, v, mono, highlight }) {
  return (
    <div className="flex justify-between gap-2 py-0.5">
      <span className="text-[#737373] text-[10px]">{k}</span>
      <span
        className={`${mono ? "font-mono" : ""} ${highlight ? "text-emerald-400 font-bold" : "text-[#D4D4D8]"} text-[10px] truncate max-w-[160px]`}
        title={typeof v === "string" ? v : undefined}
      >
        {v != null && v !== "" ? v : "—"}
      </span>
    </div>
  );
}
